import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  connectForgejo,
  connectForgejoRepo,
  disconnectForgejo,
  disconnectForgejoRepo,
  verifyForgejo,
  type ConnectForgejoResponse,
  type ConnectRepoResponse,
  type DisconnectForgejoRepoResponse,
  type DisconnectForgejoResponse,
  type VerifyForgejoResponse,
} from "./api";
import { ForgejoConnectedView } from "./ForgejoConnectedView";
import {
  describeForgejoError,
  describeForgejoFormError,
} from "./forgejoErrors";
import { useConnectedForgejoRepos, useForgejoRepos } from "./useForgejoRepos";
import { useForgejoStatus } from "./useForgejoStatus";

// Секция настроек Forgejo на `/settings/connections`.
//
// Два режима, переключаются по `status.connected`:
// 1. NOT CONNECTED — форма URL + PAT, кнопки «Проверить» и «Подключить».
// 2. CONNECTED — host_url + список репо + список подключённых; вынесено в
//    `ForgejoConnectedView.tsx`.
//
// Disconnect-операции: «Отключить Forgejo» в заголовке + «×» на
// строке подключённого репо. Обе — через Mantine Modal-confirm.
export function ForgejoSection(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Form state: храним host_url/pat в одном объекте — два поля
  // подаются вместе и `/verify` и `/connect`, нет смысла плодить
  // useState. PAT в `<PasswordInput>` — скрывает символы и блокирует
  // autofill менеджеров паролей.
  const [hostUrl, setHostUrl] = useState("");
  const [pat, setPat] = useState("");

  // Локальный feedback. alert-state очищается при следующем submit'е.
  const [verifyUsername, setVerifyUsername] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Inline-ошибки полей: клиентская проверка пустоты ДО запроса и отказ
  // 422 сервера по форме адреса или токена. Паттерн
  // error-проп Mantine — как в `selfImprovement/configUpdate.tsx`.
  const [urlError, setUrlError] = useState<string | null>(null);
  const [patError, setPatError] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  // Disconnect-секция: confirm-state + success-сообщение.
  // Notifications-стек не подключали — выводим Alert'ом как connect.
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [repoPendingDisconnect, setRepoPendingDisconnect] = useState<
    string | null
  >(null);
  const [disconnectInfo, setDisconnectInfo] = useState<string | null>(null);

  const status = useForgejoStatus();
  const isConnected = status.data?.connected === true;

  const repos = useForgejoRepos(isConnected);
  const connectedRepos = useConnectedForgejoRepos(isConnected);

  // Отказ verify/PUT: `422` по `detail` ложится на поле адреса или
  // токена, остальное — в Alert формы.
  const showFormError = (err: unknown): void => {
    const formError = describeForgejoFormError(err, t);
    if (formError.target === "hostUrl") {
      setUrlError(formError.message);
      return;
    }
    if (formError.target === "pat") {
      setPatError(formError.message);
      return;
    }
    setSubmitError(formError.message);
  };

  // Клиентская валидация формы перед /verify и /connect: оба поля
  // обязательны после trim. Пустой URL → urlRequired, пустой токен →
  // patRequired. `host_url` уходит как введён: внешние пробелы снимает
  // сервер, он же проверяет форму адреса и отвечает 422. Токен уходит
  // без внешних пробельных символов: сервер их не обрезает, а валидный
  // токен Forgejo их не содержит. Отказ 422 сервера выводится на поле
  // по `detail` (forgejoErrors.ts).
  const validateForm = (): boolean => {
    const urlEmpty = hostUrl.trim() === "";
    const patEmpty = pat.trim() === "";
    setUrlError(urlEmpty ? t("connections.urlRequired") : null);
    setPatError(patEmpty ? t("connections.patRequired") : null);
    return !urlEmpty && !patEmpty;
  };

  const verifyMutation = useMutation<
    VerifyForgejoResponse,
    Error,
    { host_url: string; pat: string }
  >({
    mutationFn: (body) => verifyForgejo(body),
    onSuccess: (data) => {
      setVerifyUsername(data.username);
      setSubmitError(null);
    },
    onError: (err) => {
      setVerifyUsername(null);
      showFormError(err);
    },
  });

  const connectMutation = useMutation<
    ConnectForgejoResponse,
    Error,
    { host_url: string; pat: string }
  >({
    mutationFn: (body) => connectForgejo(body),
    onSuccess: () => {
      setVerifyUsername(null);
      setSubmitError(null);
      setPat("");
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "status"],
      });
    },
    onError: (err) => {
      showFormError(err);
    },
  });

  const connectRepoMutation = useMutation<
    ConnectRepoResponse,
    Error,
    { repo: string }
  >({
    mutationFn: (body) => connectForgejoRepo(body),
    onSuccess: (data) => {
      setWebhookUrl(data.webhook_url);
      // Префикс матчит и список репозиториев, и список подключённых:
      // ключ второго — продолжение первого.
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "repos"],
      });
    },
    onError: (err) => {
      setWebhookUrl(null);
      setSubmitError(describeForgejoError(err, t));
    },
  });

  // Полное отключение Forgejo. После success'а инвалидируем
  // status + repos queries — следующий render увидит connected=false и
  // переключится в форму. Idempotent (disconnected=false) → нейтральное
  // «не было настроено».
  const disconnectMutation = useMutation<
    DisconnectForgejoResponse,
    Error,
    void
  >({
    mutationFn: () => disconnectForgejo(),
    onSuccess: (data) => {
      setSubmitError(null);
      setWebhookUrl(null);
      if (data.disconnected) {
        setDisconnectInfo(
          t("connections.forgejo.disconnect.success", {
            count: data.webhooksRemoved,
          }),
        );
      } else {
        setDisconnectInfo(
          t("connections.forgejo.disconnect.alreadyDisconnected"),
        );
      }
      setConfirmDisconnectOpen(false);
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "status"],
      });
      // Префикс матчит и список репозиториев, и список подключённых:
      // ключ второго — продолжение первого.
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "repos"],
      });
    },
    onError: (err) => {
      setDisconnectInfo(null);
      setSubmitError(
        t("connections.forgejo.disconnect.error", {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
      setConfirmDisconnectOpen(false);
    },
  });

  // Отвязка одного репо. Invalidate обоих repo-query'ев: на
  // строке исчезнет «Подключён»-Badge, репо уйдёт из connected-list'а.
  const disconnectRepoMutation = useMutation<
    DisconnectForgejoRepoResponse,
    Error,
    { repo: string }
  >({
    mutationFn: ({ repo }) => disconnectForgejoRepo(repo),
    onSuccess: (data, variables) => {
      setSubmitError(null);
      setWebhookUrl(null);
      if (data.disconnected) {
        setDisconnectInfo(
          t("connections.forgejo.disconnectRepo.success", {
            repo: variables.repo,
          }),
        );
      } else {
        setDisconnectInfo(
          t("connections.forgejo.disconnectRepo.alreadyDisconnected", {
            repo: variables.repo,
          }),
        );
      }
      setRepoPendingDisconnect(null);
      // Префикс матчит и список репозиториев, и список подключённых:
      // ключ второго — продолжение первого.
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "repos"],
      });
    },
    onError: (err) => {
      setDisconnectInfo(null);
      setSubmitError(
        t("connections.forgejo.disconnectRepo.error", {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
      setRepoPendingDisconnect(null);
    },
  });

  // Cancel-handler'ы выделены, чтобы их можно было передать одновременно
  // в Button.onClick (юзер кликнул «Отмена») и в Modal.onClose (юзер
  // нажал Esc / клик по backdrop). Иначе вторая ветка осталась бы
  // непокрытой v8-coverage'ом из тестов.
  const handleCancelDisconnect = (): void => {
    setConfirmDisconnectOpen(false);
  };
  const handleCancelDisconnectRepo = (): void => {
    setRepoPendingDisconnect(null);
  };

  if (status.isPending) {
    return <Loader />;
  }

  if (status.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("connections.forgejoTitle")}</Title>
        <Alert color="red" title={t("connections.errorHeading")}>
          {t("connections.loadingError")}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t("connections.forgejoTitle")}</Title>
        {isConnected ? (
          <Button
            color="red"
            variant="subtle"
            onClick={() => {
              setSubmitError(null);
              setDisconnectInfo(null);
              setConfirmDisconnectOpen(true);
            }}
            loading={disconnectMutation.isPending}
          >
            {t("connections.forgejo.disconnect.button")}
          </Button>
        ) : null}
      </Group>

      {isConnected ? (
        <ForgejoConnectedView
          status={status.data}
          reposQuery={repos}
          connectedReposQuery={connectedRepos}
          webhookUrl={webhookUrl}
          disconnectInfo={disconnectInfo}
          submitError={submitError}
          disconnectRepoPendingRepo={
            disconnectRepoMutation.variables?.repo ?? undefined
          }
          disconnectRepoIsPending={disconnectRepoMutation.isPending}
          connectRepoPendingRepo={
            connectRepoMutation.variables?.repo ?? undefined
          }
          connectRepoIsPending={connectRepoMutation.isPending}
          onRequestDisconnectRepo={(repo) => {
            setSubmitError(null);
            setDisconnectInfo(null);
            setRepoPendingDisconnect(repo);
          }}
          onConnectRepo={(repo) => {
            setSubmitError(null);
            setWebhookUrl(null);
            connectRepoMutation.mutate({ repo });
          }}
        />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("connections.urlLabel")}
            placeholder={t("connections.urlPlaceholder")}
            value={hostUrl}
            onChange={(event) => {
              setHostUrl(event.currentTarget.value);
              setUrlError(null);
            }}
            error={urlError}
          />
          <PasswordInput
            label={t("connections.patLabel")}
            description={t("connections.patHint")}
            value={pat}
            onChange={(event) => {
              setPat(event.currentTarget.value);
              setPatError(null);
            }}
            error={patError}
            // PasswordInput Mantine сам не ставит aria-invalid при ошибке.
            aria-invalid={patError !== null}
          />

          {verifyUsername !== null ? (
            <Alert color="green">
              {t("connections.verifySuccess", { username: verifyUsername })}
            </Alert>
          ) : null}

          {disconnectInfo !== null ? (
            <Alert color="blue">{disconnectInfo}</Alert>
          ) : null}

          {submitError !== null ? (
            <Alert color="red" title={t("connections.errorHeading")}>
              {submitError}
            </Alert>
          ) : null}

          <Group>
            <Button
              variant="default"
              onClick={() => {
                setSubmitError(null);
                setVerifyUsername(null);
                if (!validateForm()) {
                  return;
                }
                verifyMutation.mutate({ host_url: hostUrl, pat: pat.trim() });
              }}
              loading={verifyMutation.isPending}
            >
              {t("connections.verifyButton")}
            </Button>
            <Button
              onClick={() => {
                setSubmitError(null);
                setVerifyUsername(null);
                if (!validateForm()) {
                  return;
                }
                connectMutation.mutate({ host_url: hostUrl, pat: pat.trim() });
              }}
              loading={connectMutation.isPending}
            >
              {t("connections.connectButton")}
            </Button>
          </Group>
        </Stack>
      )}

      <Modal
        opened={confirmDisconnectOpen}
        onClose={handleCancelDisconnect}
        title={t("connections.forgejo.disconnect.confirmTitle")}
        centered
      >
        <Stack gap="md">
          <Text>{t("connections.forgejo.disconnect.confirmText")}</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCancelDisconnect}>
              {t("connections.modalCancel")}
            </Button>
            <Button
              color="red"
              onClick={() => {
                setSubmitError(null);
                setDisconnectInfo(null);
                disconnectMutation.mutate();
              }}
              loading={disconnectMutation.isPending}
            >
              {t("connections.modalConfirm")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={repoPendingDisconnect !== null}
        onClose={handleCancelDisconnectRepo}
        title={t("connections.forgejo.disconnectRepo.confirmTitle")}
        centered
      >
        {repoPendingDisconnect !== null ? (
          <Stack gap="md">
            <Text>
              {t("connections.forgejo.disconnectRepo.confirmText", {
                repo: repoPendingDisconnect,
              })}
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancelDisconnectRepo}>
                {t("connections.modalCancel")}
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setSubmitError(null);
                  setDisconnectInfo(null);
                  disconnectRepoMutation.mutate({
                    repo: repoPendingDisconnect,
                  });
                }}
                loading={disconnectRepoMutation.isPending}
              >
                {t("connections.modalConfirm")}
              </Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
